import type { FC, ReactNode } from 'react'
import type { GetStaticProps } from 'next'
import type { Category, Product, StoreSettings } from '@/types'

import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { BoutiqueHero } from '@/components/landing/boutique-hero'
import { loadCatalog } from '@/lib/catalog'
import { CUSTOM_CATEGORY, LIMITED_CATEGORY } from '@/constants/site'

import {
    CollectionsSection,
    CustomSection,
    FavouritesSection,
    GallerySection,
    HowToOrderSection,
    LimitedSection,
    NewArrivalsSection,
    StorySection
} from '@/components/landing/home-sections'

interface HomeProps {
    products: Array<Product>
    categories: Array<Category>
    settings: StoreSettings
}

const byNewest = (a: Product, b: Product) => (a.createdAt < b.createdAt ? 1 : a.createdAt > b.createdAt ? -1 : b.sortOrder - a.sortOrder)

const Home: FC<HomeProps> = ({ products, categories }): ReactNode => {
    const limitedCategory = categories.find(category => category.slug === LIMITED_CATEGORY) ?? null
    const customCategory = categories.find(category => category.slug === CUSTOM_CATEGORY) ?? null
    const storyDoll = products.find(product => product.isFeatured && product.designKey && !product.designKey.startsWith('keychain'))
        ?? products.find(product => product.image)
        ?? products[0]
        ?? null
    const customDoll = products.find(product => product.category?.slug === CUSTOM_CATEGORY || product.labels.includes('custom')) ?? null
    const limited = products.filter(product => product.category?.slug === LIMITED_CATEGORY || product.labels.includes('edisi-terbatas'))
    const labelledNew = products.filter(product => product.labels.includes('terbaru'))
    const newest = (labelledNew.length >= 4 ? labelledNew : [...products]).sort(byNewest).slice(0, 8)

    return (
        <Layout variant='overlay' categories={categories}>
            <Seo />

            <noscript>
                <style>{'.cw-loader{display:none!important}.cw-hero-copy [data-hero-reveal]{opacity:1!important;visibility:visible!important}'}</style>
            </noscript>

            <BoutiqueHero products={products} categories={categories} />
            <CollectionsSection categories={categories} products={products} />
            <NewArrivalsSection products={newest} />
            <FavouritesSection products={products} />
            <StorySection product={storyDoll} />
            <LimitedSection products={limited} category={limitedCategory} />
            <GallerySection products={products} />
            <CustomSection product={customDoll} category={customCategory} />
            <HowToOrderSection />
        </Layout>
    )
}

export const getStaticProps: GetStaticProps<HomeProps> = async () => {
    const { products, categories, settings, ok } = await loadCatalog()

    return {
        props: { products, categories, settings },
        revalidate: ok ? 30 : 10
    }
}

export default Home
